import React from "react";
import { CheckCircle2, Circle, Loader2 } from "lucide-react";
import {
  OFFICIAL_STATUS_LABEL,
  OFFICIAL_STATUS_SEQUENCE,
  OFFICIAL_STATUS_PROGRESS,
} from "../../lib/constants";

/**
 * Timeline visual dos 12 estados canônicos RAF Direct para a Família.
 *
 * @param {string} current  official_status do caso (ex: "RESERVA")
 * @param {number} progress progress_percent do caso (0..100). Se undefined, deriva do current.
 */
export const CanonicalTimeline = ({ current, progress }) => {
  const currentIdx = OFFICIAL_STATUS_SEQUENCE.indexOf(current);
  const pct = typeof progress === "number"
    ? progress
    : OFFICIAL_STATUS_PROGRESS[current] || 0;

  return (
    <div data-testid="canonical-timeline">
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="text-xs uppercase tracking-widest text-slate-500">Etapa atual</div>
        <div className="font-mono text-xs text-slate-500">{pct}%</div>
      </div>
      <div className="text-sm font-display font-semibold text-[#0B2B62] mb-3" data-testid="ct-current-label">
        {OFFICIAL_STATUS_LABEL[current] || "—"}
      </div>

      {/* Barra de progresso oficial RAF Direct */}
      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden mb-5">
        <div
          className="h-full bg-gradient-to-r from-[#0B2B62] to-[#1E40AF] transition-all duration-500"
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
          data-testid="ct-progress-bar"
        />
      </div>

      {/* Lista dos 12 estados canônicos */}
      <ol className="relative">
        {OFFICIAL_STATUS_SEQUENCE.map((st, i) => {
          // Iter 68.11 — quando o caso está 100% concluído, TODOS os steps
          // (incluindo o último) devem aparecer como "done" verde, sem o
          // texto "Estamos aqui agora…". Antes disso, ficam done/active/pending.
          const isConcluded = pct >= 100;
          const done = isConcluded || (currentIdx >= 0 && i < currentIdx);
          const active = !isConcluded && i === currentIdx;
          const IconEl = done ? CheckCircle2 : (active ? Loader2 : Circle);
          const iconCls = done
            ? "text-green-600"
            : active
              ? "text-[#0B2B62] animate-spin-slow"
              : "text-slate-300";
          const textCls = done
            ? "text-slate-700"
            : active
              ? "text-[#0B2B62] font-semibold"
              : "text-slate-400";

          return (
            <li
              key={st}
              data-testid={`ct-step-${st}`}
              data-state={done ? "done" : active ? "active" : "pending"}
              className="pl-8 pb-3.5 relative"
            >
              <span className="absolute left-1.5 top-0.5">
                <IconEl className={`w-4 h-4 ${iconCls}`} strokeWidth={active ? 2.2 : 2} />
              </span>
              {i < OFFICIAL_STATUS_SEQUENCE.length - 1 && (
                <span className="absolute left-[13px] top-5 bottom-0 w-px bg-slate-200" />
              )}
              <div className={`text-xs ${textCls}`}>
                {OFFICIAL_STATUS_LABEL[st]}
              </div>
              {active && (
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Estamos aqui agora — sua família será avisada assim que houver avanço.
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};
